import { ref } from "vue";
import { Mark, mergeAttributes } from "@tiptap/core";

// ============================================================================
// HighlightMark TipTap extension
// ============================================================================

export const HighlightMark = Mark.create({
  name: "highlight",
  addOptions() {
    return { multicolor: true };
  },
  addAttributes() {
    return {
      color: {
        default: null,
        parseHTML: (element) => element.style.backgroundColor || null,
        renderHTML: (attributes) => (attributes.color ? { style: `background-color: ${attributes.color}` } : {}),
      },
    };
  },
  parseHTML() {
    return [{ tag: "span[style]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes), 0];
  },
  addCommands() {
    return {
      setHighlight:
        (color = "#fff8c5") =>
        ({ chain }) =>
          chain().setMark(this.name, { color }),
      unsetHighlight:
        () =>
        ({ chain }) =>
          chain().unsetMark(this.name),
    };
  },
});

// ============================================================================
// Composable
// ============================================================================

const HIGHLIGHT_DISPLAY_DEFAULT = "transparent";
const HIGHLIGHT_INPUT_FALLBACK = "#fff8c5";

/**
 * @param {Object} options
 * @param {import('vue').Ref} options.editor - TipTap editor ref
 */
export function useEditorHighlight({ editor }) {
  const selectedColor = ref("#000000");
  const selectedHighlight = ref(HIGHLIGHT_INPUT_FALLBACK);
  const selectedHighlightDisplay = ref(HIGHLIGHT_DISPLAY_DEFAULT);
  const lastSelection = ref(null);
  const showHighlightPanel = ref(false);

  const highlightPalette = [
    "#000000", "#808080", "#C0C0C0", "#FFFFFF",
    "#FFEB3B", "#FFF59D", "#FFF9C4", "#FFD54F",
    "#FFC107", "#FFA000", "#FF9800", "#FF7043",
    "#FF5722", "#F44336", "#E91E63", "#AD1457",
    "#9C27B0", "#7B1FA2", "#673AB7", "#3F51B5",
    "#2196F3", "#03A9F4", "#00BCD4", "#009688",
    "#4CAF50", "#8BC34A", "#CDDC39", "#AED581",
    "#795548", "#A1887F", "#6D4C41", "#4E342E",
  ];

  const toHexColor = (value, fallback = "#000000") => {
    if (!value || typeof value !== "string") return fallback;
    const trimmed = value.trim();
    if (/^#([0-9a-fA-F]{6})$/.test(trimmed)) return trimmed.toLowerCase();
    if (/^#([0-9a-fA-F]{3})$/.test(trimmed)) {
      return (
        "#" +
        trimmed[1] + trimmed[1] +
        trimmed[2] + trimmed[2] +
        trimmed[3] + trimmed[3]
      ).toLowerCase();
    }
    const rgbMatch = trimmed.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgbMatch) {
      const [r, g, b] = rgbMatch.slice(1, 4).map((n) => {
        const num = Number.parseInt(n, 10);
        return Math.max(0, Math.min(255, Number.isNaN(num) ? 0 : num));
      });
      const toHex = (num) => num.toString(16).padStart(2, "0");
      return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    }
    return fallback;
  };

  const updateColorState = (editorInstance) => {
    if (!editorInstance) return;
    const { color } = editorInstance.getAttributes("textStyle");
    selectedColor.value = toHexColor(color || "#000000", "#000000");
    const { color: highlightColor } = editorInstance.getAttributes("highlight");
    selectedHighlight.value = toHexColor(highlightColor || HIGHLIGHT_INPUT_FALLBACK, HIGHLIGHT_INPUT_FALLBACK);
    selectedHighlightDisplay.value = highlightColor || HIGHLIGHT_DISPLAY_DEFAULT;
  };

  const saveSelection = () => {
    const instance = editor.value;
    if (instance) {
      const { from, to } = instance.state.selection;
      lastSelection.value = { from, to };
    }
  };

  const openColorPicker = () => saveSelection();

  const toggleHighlightPanel = () => {
    saveSelection();
    showHighlightPanel.value = !showHighlightPanel.value;
  };

  const handleColorInput = (nextColor) => {
    const instance = editor.value;
    if (!instance || !nextColor) return;
    selectedColor.value = nextColor;
    let chain = instance.chain().focus();
    const sel = lastSelection.value;
    if (sel?.from != null && sel?.to != null) chain = chain.setTextSelection(sel);
    chain.setColor(nextColor).run();
    lastSelection.value = null;
  };

  const handleHighlightInput = (nextColor) => {
    const instance = editor.value;
    if (!instance || !nextColor) return;
    selectedHighlight.value = nextColor;
    selectedHighlightDisplay.value = nextColor;
    let chain = instance.chain().focus();
    const sel = lastSelection.value;
    if (sel?.from != null && sel?.to != null) chain = chain.setTextSelection(sel);
    chain.setHighlight(nextColor).run();
    lastSelection.value = null;
    showHighlightPanel.value = false;
  };

  const handleHighlightSelect = (color) => {
    const instance = editor.value;
    if (!instance) return;
    selectedHighlight.value = color;
    selectedHighlightDisplay.value = color;
    let chain = instance.chain().focus();
    const sel = lastSelection.value;
    if (sel?.from != null && sel?.to != null) chain = chain.setTextSelection(sel);
    chain.setHighlight(color).run();
    lastSelection.value = null;
    showHighlightPanel.value = false;
  };

  const handleHighlightNone = () => {
    const instance = editor.value;
    if (!instance) return;
    const { from, to } = instance.state.selection;
    let chain = instance.chain().focus();
    if (from != null && to != null) chain = chain.setTextSelection({ from, to });
    chain.unsetHighlight().run();
    selectedHighlight.value = HIGHLIGHT_INPUT_FALLBACK;
    selectedHighlightDisplay.value = HIGHLIGHT_DISPLAY_DEFAULT;
    lastSelection.value = null;
    showHighlightPanel.value = false;
  };

  return {
    selectedColor,
    selectedHighlight,
    selectedHighlightDisplay,
    showHighlightPanel,
    highlightPalette,
    updateColorState,
    openColorPicker,
    toggleHighlightPanel,
    handleColorInput,
    handleHighlightInput,
    handleHighlightSelect,
    handleHighlightNone,
  };
}
